// Bottom bar: what you can buy, and actions on the current selection.
import type { Balance, Id, World } from '../sim';
import type { Pick } from '../render/entities';

export type ShopAction =
  | { t: 'place'; what: 'substation' | 'thermal' }
  | { t: 'move'; id: Id }
  | { t: 'upgradeTransformer'; id: Id }
  | { t: 'upgradeLine'; id: Id }
  | { t: 'untap'; id: Id }
  | { t: 'cancel' };

export interface Shop {
  update(world: World, selected: Pick | null, mode: string, hint: string): void;
}

export function createShop(
  parent: HTMLElement,
  balance: Balance,
  act: (a: ShopAction) => void,
): Shop {
  const el = document.createElement('div');
  el.className = 'shop';
  parent.appendChild(el);
  const hintEl = document.createElement('div');
  hintEl.className = 'hint';
  parent.appendChild(hintEl);

  let lastKey = '';

  function button(
    label: string,
    cost: number | null,
    action: ShopAction,
    disabled = false,
    cls = '',
  ): HTMLButtonElement {
    const b = document.createElement('button');
    b.className = `btn ${cls}`;
    b.innerHTML = cost === null ? label : `${label} <span class="cost">$${cost}</span>`;
    b.disabled = disabled;
    b.addEventListener('click', () => act(action));
    return b;
  }

  return {
    update(world, selected, mode, hint) {
      hintEl.textContent = hint;
      hintEl.classList.toggle('show', hint.length > 0);
      const key = `${selected?.kind ?? ''}:${selected?.id ?? ''}|${mode}|${Math.floor(world.cash)}|${world.taps.length}|${world.status}`;
      if (key === lastKey) return;
      lastKey = key;
      el.innerHTML = '';
      const cash = world.cash;

      if (mode !== 'idle') {
        el.appendChild(button('Cancel', null, { t: 'cancel' }, false, 'btn-ghost'));
        return;
      }

      if (selected?.kind === 'substation') {
        const s = world.substations.find((x) => x.id === selected.id);
        if (s) {
          const info = document.createElement('span');
          info.className = 'shop-info';
          info.textContent = `Substation · ${s.capacity} cap`;
          el.appendChild(info);
          el.appendChild(
            button(
              'Move',
              balance.substation.moveFee,
              { t: 'move', id: s.id },
              cash < balance.substation.moveFee,
            ),
          );
          el.appendChild(
            button(
              'Bigger transformer',
              balance.substation.transformerUpgradeCost,
              { t: 'upgradeTransformer', id: s.id },
              cash < balance.substation.transformerUpgradeCost,
            ),
          );
          el.appendChild(button('Done', null, { t: 'cancel' }, false, 'btn-ghost'));
        }
        return;
      }

      if (selected?.kind === 'plant') {
        const p = world.plants.find((x) => x.id === selected.id);
        if (p) {
          const used = world.taps.filter((t) => t.plantId === p.id).length;
          const info = document.createElement('span');
          info.className = 'shop-info';
          info.textContent = `${p.type[0]?.toUpperCase()}${p.type.slice(1)} plant · ${used}/${p.maxTaps} lines`;
          el.appendChild(info);
          el.appendChild(button('Done', null, { t: 'cancel' }, false, 'btn-ghost'));
        }
        return;
      }

      if (selected?.kind === 'tap') {
        const t = world.taps.find((x) => x.id === selected.id);
        if (t) {
          const info = document.createElement('span');
          info.className = 'shop-info';
          info.textContent = t.highVoltage ? 'High-voltage line' : 'Line';
          el.appendChild(info);
          if (!t.highVoltage) {
            el.appendChild(
              button(
                'High voltage',
                balance.line.highVoltageCost,
                { t: 'upgradeLine', id: t.id },
                cash < balance.line.highVoltageCost,
              ),
            );
          }
          el.appendChild(button('Remove', null, { t: 'untap', id: t.id }, false, 'btn-danger'));
          el.appendChild(button('Done', null, { t: 'cancel' }, false, 'btn-ghost'));
        }
        return;
      }

      el.appendChild(
        button(
          'Substation',
          balance.substation.cost,
          { t: 'place', what: 'substation' },
          cash < balance.substation.cost,
        ),
      );
      el.appendChild(
        button(
          'Thermal plant',
          balance.plants.thermal.cost,
          { t: 'place', what: 'thermal' },
          cash < balance.plants.thermal.cost,
        ),
      );
    },
  };
}
